'use client';

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";

/**
 * Drawer — side sheet built on Radix Dialog.
 *
 * Sides:
 *   left  | right       — physical side (LTR default)
 *   start | end         — logical side (LTR: start=left; RTL: start=right)
 *   top   | bottom      — vertical sheets
 *
 * Sizes: narrow (320px) | wide (480px) | full (100vw or 100vh)
 *
 * Backdrop opacity is controlled via `backdropClassName` on ModalOverlay or
 * the `overlayClassName` prop below.
 */

export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;
export const DrawerPortal = DialogPrimitive.Portal;

export const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DrawerOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="drawer-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-ink-primary/50 backdrop-blur-sm",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
});
DrawerOverlay.displayName = "DrawerOverlay";

type DrawerSide = "left" | "right" | "top" | "bottom" | "start" | "end";
type DrawerSize = "narrow" | "wide" | "full";

const drawerVariants = cva(
  [
    "fixed z-50 flex flex-col bg-chrome-surface text-ink-primary",
    "border-chrome-border shadow-xl",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "data-[state=closed]:duration-standard data-[state=open]:duration-standard",
  ].join(" "),
  {
    variants: {
      side: {
        left:
          "inset-y-0 left-0 h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        right:
          "inset-y-0 right-0 h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
        top:
          "inset-x-0 top-0 w-full border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 w-full border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
      },
      size: {
        narrow: "",
        wide: "",
        full: "",
      },
    },
    compoundVariants: [
      // Horizontal drawers use width; vertical drawers use height.
      { side: ["left", "right"], size: "narrow", className: "w-[320px]" },
      { side: ["left", "right"], size: "wide", className: "w-[480px]" },
      { side: ["left", "right"], size: "full", className: "w-screen" },
      { side: ["top", "bottom"], size: "narrow", className: "h-[240px]" },
      { side: ["top", "bottom"], size: "wide", className: "h-[400px]" },
      { side: ["top", "bottom"], size: "full", className: "h-screen" },
    ],
    defaultVariants: { side: "right", size: "narrow" },
  },
);

function resolvePhysicalSide(
  side: DrawerSide,
  rtl: boolean,
): "left" | "right" | "top" | "bottom" {
  if (side === "start") return rtl ? "right" : "left";
  if (side === "end") return rtl ? "left" : "right";
  return side;
}

function useDirection(): "ltr" | "rtl" {
  const [dir, setDir] = React.useState<"ltr" | "rtl">("ltr");
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const compute = () =>
      (document.documentElement.getAttribute("dir") ?? "ltr") === "rtl"
        ? "rtl"
        : "ltr";
    setDir(compute());
  }, []);
  return dir;
}

export interface DrawerContentProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
      "children"
    >,
    Omit<VariantProps<typeof drawerVariants>, "side"> {
  /**
   * Side to slide from. Prefer `start` / `end` for RTL-aware layouts;
   * physical values (`left`/`right`/`top`/`bottom`) are also supported.
   */
  side?: DrawerSide;
  showClose?: boolean;
  overlayClassName?: string;
  children?: React.ReactNode;
}

export const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(function DrawerContent(
  {
    className,
    children,
    side = "end",
    size = "narrow",
    showClose = true,
    overlayClassName,
    ...props
  },
  ref,
) {
  const dir = useDirection();
  const physicalSide = resolvePhysicalSide(side, dir === "rtl");
  return (
    <DrawerPortal>
      <DrawerOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="drawer-content"
        data-side={physicalSide}
        data-logical-side={side}
        data-size={size}
        className={cn(drawerVariants({ side: physicalSide, size, className }))}
        {...props}
      >
        {children}
        {showClose && (
          <DialogPrimitive.Close
            aria-label="Close"
            className={cn(
              "absolute end-space-16 top-space-16 rounded-chip",
              "text-ink-muted hover:text-ink-primary",
              "outline-none focus-visible:ring-2 focus-visible:ring-accent-violet",
              "transition-colors duration-micro ease-standard",
            )}
          >
            <X className="size-4" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DrawerPortal>
  );
});
DrawerContent.displayName = "DrawerContent";

export const DrawerHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function DrawerHeader({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-slot="drawer-header"
      className={cn(
        "flex flex-col gap-space-4 p-space-20 border-b border-chrome-border",
        className,
      )}
      {...props}
    />
  );
});
DrawerHeader.displayName = "DrawerHeader";

export const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DrawerTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      data-slot="drawer-title"
      className={cn("text-heading-22 text-ink-primary", className)}
      {...props}
    />
  );
});
DrawerTitle.displayName = "DrawerTitle";

export const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DrawerDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      data-slot="drawer-description"
      className={cn("text-body-14 text-ink-muted", className)}
      {...props}
    />
  );
});
DrawerDescription.displayName = "DrawerDescription";

export const DrawerBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function DrawerBody({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-slot="drawer-body"
      className={cn("flex-1 overflow-y-auto p-space-20", className)}
      {...props}
    />
  );
});
DrawerBody.displayName = "DrawerBody";

export const DrawerFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function DrawerFooter({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      data-slot="drawer-footer"
      className={cn(
        "flex items-center justify-end gap-space-8 p-space-20 border-t border-chrome-border",
        className,
      )}
      {...props}
    />
  );
});
DrawerFooter.displayName = "DrawerFooter";

export { drawerVariants };
